import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Upload, FileText, Download, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

export default function Documenti() {
  const [docs, setDocs] = useState([]);
  const [practices, setPractices] = useState([]);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);
  const [docType, setDocType] = useState("altro");
  const [projectId, setProjectId] = useState("_none");
  const [ocrId, setOcrId] = useState(null);
  const [ocrText, setOcrText] = useState("");
  const [ocrProgress, setOcrProgress] = useState(0);

  const runOcr = async (doc) => {
    const isImg = (doc.mime_type || "").startsWith("image/");
    const isPdf = (doc.mime_type || "").includes("pdf");
    if (!isImg && !isPdf) { toast.error("OCR disponibile solo per immagini/PDF"); return; }
    setOcrId(doc.id); setOcrText(""); setOcrProgress(0);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`;
      const { data } = await Tesseract.recognize(url, "ita", {
        logger: m => { if (m.status === "recognizing text") setOcrProgress(Math.round(m.progress * 100)); }
      });
      setOcrText(data.text);
      toast.success("OCR completato");
    } catch (e) { toast.error("Errore OCR: " + e.message); setOcrId(null); }
  };

  const load = async () => { const { data } = await api.get(`/documents${q ? `?q=${q}` : ""}`); setDocs(data); };
  useEffect(() => { load(); }, [q]);
  useEffect(() => { api.get("/practices").then(r => setPractices(r.data)); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("doc_type", docType);
    if (projectId !== "_none") fd.append("project_id", projectId);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      load();
    } catch { toast.error("Errore upload"); }
    e.target.value = "";
  };

  const remove = async (id) => { await api.delete(`/documents/${id}`); load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Documenti</h1>
          <p className="text-sm text-slate-500 mt-1">{docs.length} documenti in archivio</p>
        </div>
      </div>

      <Card className="p-5">
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Tipo documento</label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                {["atto","contratto","corrispondenza","parere","template","altro"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Pratica collegata</label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Nessuna</SelectItem>
                {practices.map(p => <SelectItem key={p.id} value={p.id}>{p.numero_protocollo} · {p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex items-end gap-2">
            <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid="file-input" />
            <Button onClick={()=>fileRef.current?.click()} className="bg-navy-800 hover:bg-navy-900 text-white" data-testid="upload-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Carica documento
            </Button>
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <Input className="pl-9" placeholder="Cerca documenti..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="docs-search" />
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-2.5">Nome</th>
              <th className="text-left px-5 py-2.5">Tipo</th>
              <th className="text-left px-5 py-2.5">Versione</th>
              <th className="text-left px-5 py-2.5">Caricato da</th>
              <th className="text-left px-5 py-2.5">Data</th>
              <th className="text-right px-5 py-2.5">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`doc-row-${d.id}`}>
                <td className="px-5 py-3"><div className="flex items-center gap-2 text-navy-900 font-medium"><FileText className="w-4 h-4 text-navy-600" />{d.name}</div></td>
                <td className="px-5 py-3"><Badge variant="outline" className="capitalize">{d.doc_type}</Badge></td>
                <td className="px-5 py-3 font-mono text-xs">v{d.version_number}</td>
                <td className="px-5 py-3 text-slate-600">{d.uploaded_by_name || "—"}</td>
                <td className="px-5 py-3 text-slate-600">{new Date(d.created_at).toLocaleDateString("it-IT")}</td>
                <td className="px-5 py-3 text-right">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-navy-600 hover:underline mr-3"><Download className="w-3 h-3" /> Scarica</a>
                  <button onClick={()=>runOcr(d)} className="inline-flex items-center gap-1 text-xs text-amber-600 hover:underline mr-3" data-testid={`ocr-${d.id}`}><Sparkles className="w-3 h-3" />OCR</button>
                  <button onClick={()=>remove(d.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-doc-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {docs.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-12">Nessun documento.</td></tr>}
          </tbody>
        </table>
      </Card>

      {ocrId && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-semibold text-navy-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Testo OCR estratto</div>
            <Button variant="ghost" size="sm" onClick={()=>{ setOcrId(null); setOcrText(""); }}>Chiudi</Button>
          </div>
          {ocrText ? (
            <textarea readOnly className="w-full h-64 text-xs font-mono p-3 border rounded-md bg-slate-50" value={ocrText} />
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Elaborazione OCR... {ocrProgress}%
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
